import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ArrowCounterClockwise, CalendarDots, X } from "@phosphor-icons/react";

import { TherapistCombobox } from "@/components/ui/therapist-combobox";
import { formatDateLong, toLocalISODate } from "./payout-helpers";

export type PayoutFilters = {
  therapistId: string | null;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
};

export const emptyPayoutFilters: PayoutFilters = {
  therapistId: null,
  dateFrom: undefined,
  dateTo: undefined,
};

export function hasAnyPayoutFilter(filters: PayoutFilters): boolean {
  return !!(filters.therapistId || filters.dateFrom || filters.dateTo);
}

export function buildPayoutParams(
  page: number,
  limit: number,
  filters: PayoutFilters,
): URLSearchParams {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (filters.therapistId) params.set("therapistId", filters.therapistId);
  if (filters.dateFrom) params.set("dateFrom", toLocalISODate(filters.dateFrom));
  if (filters.dateTo) params.set("dateTo", toLocalISODate(filters.dateTo));
  return params;
}

interface PayoutFiltersCardProps {
  filters: PayoutFilters;
  onFilterChange: <K extends keyof PayoutFilters>(key: K, value: PayoutFilters[K]) => void;
  onClear: () => void;
}

export function PayoutFiltersCard({
  filters,
  onFilterChange,
  onClear,
}: PayoutFiltersCardProps) {
  const active = hasAnyPayoutFilter(filters);

  return (
    <div className="rounded-2xl border border-border/60 bg-card px-4 py-4 shadow-sm sm:px-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_190px_190px_auto] lg:items-end">
        <div className="sm:col-span-2 lg:col-span-1">
          <FieldLabel>Terapeut</FieldLabel>
          <TherapistCombobox
            value={filters.therapistId}
            onChange={(id) => onFilterChange("therapistId", id)}
            className="bg-card"
          />
        </div>

        <DateField
          label="Datum od"
          value={filters.dateFrom}
          onChange={(date) => onFilterChange("dateFrom", date)}
        />
        <DateField
          label="Datum do"
          value={filters.dateTo}
          onChange={(date) => onFilterChange("dateTo", date)}
        />

        <div
          className={cn(
            "flex items-center sm:col-span-2 lg:col-span-1 lg:h-10",
            !active && "hidden lg:flex",
          )}
        >
          <button
            onClick={onClear}
            disabled={!active}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-xs font-medium transition-colors",
              active
                ? "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                : "cursor-default text-muted-foreground/40",
            )}
          >
            <ArrowCounterClockwise className="size-3.5" />
            Očisti filtere
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
      {children}
    </label>
  );
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start gap-2 bg-card font-normal",
                !value && "text-muted-foreground",
                value && "pr-9",
              )}
            >
              <CalendarDots className="size-3.5 shrink-0" />
              <span className="truncate">
                {value ? formatDateLong(value) : "Odaberi datum"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-3">
            <Calendar
              mode="single"
              selected={value}
              onSelect={(date) => {
                onChange(date ?? undefined);
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
        {value && (
          <button
            onClick={() => onChange(undefined)}
            aria-label={`Ukloni ${label.toLowerCase()}`}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
